import React from 'react';
import { DocPageLayout } from '../components/DocPageLayout';
import { InfoBox } from '../components/InfoBox';
import { CodeBlock } from '../components/CodeBlock';
import { StepList } from '../components/StepList';
export function HostedSessionPage() {
  const steps = [{
    title: 'Create a payment session',
    content: <>
          <p data-id="element-1630">
            Your server creates a session by making a POST request to the
            gateway API. The session stores the payment details securely on the
            gateway.
          </p>
          <CodeBlock language="cURL" title="Create Session Request" code={`curl https://test-gateway.mastercard.com/api/rest/version/72/merchant/{merchantId}/session \\
  -u "merchant.{merchantId}:{apiPassword}" \\
  -X POST \\
  -H "Content-Type: application/json" \\
  -d '{
    "order": {
      "amount": "50.00",
      "currency": "USD"
    }
  }'`} data-id="element-1631" />
        </>
  }, {
    title: 'Configure the hosted fields',
    content: <>
          <p data-id="element-1632">
            Include the PaymentSession JavaScript library on your page and
            configure it to embed secure input fields for card number, expiry
            date, and security code.
          </p>
          <CodeBlock language="JavaScript" title="PaymentSession Configuration" code={`PaymentSession.configure({
  session: "SESSION0002abcdef1234567890",
  fields: {
    card: {
      number: "#card-number",
      securityCode: "#security-code",
      expiryMonth: "#expiry-month",
      expiryYear: "#expiry-year"
    }
  },
  frameEmbeddingMitigation: ["javascript"],
  callbacks: {
    initialized: function(response) {
      console.log("Session initialized");
    },
    formSessionUpdate: function(response) {
      if (response.status === "ok") {
        // Session updated, proceed with payment
        console.log("Session ID:", response.session.id);
      }
    }
  }
});`} data-id="element-1633" />
        </>
  }, {
    title: 'Submit the payment',
    content: <p data-id="element-1634">
          When the customer clicks "Pay", call{' '}
          <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono" data-id="element-1635">
            PaymentSession.updateSessionFromForm('card')
          </code>{' '}
          to securely send the card data to the gateway. Then use the session ID
          from your server to complete the transaction via the Pay API.
        </p>
  }, {
    title: 'Handle the response',
    content: <>
          <p data-id="element-1636">
            Process the API response on your server and display the appropriate
            result to the customer.
          </p>
          <CodeBlock language="JSON" title="Successful Response" code={`{
  "result": "SUCCESS",
  "order": {
    "id": "ORDER-12345",
    "status": "CAPTURED",
    "totalAuthorizedAmount": 50.00,
    "totalCapturedAmount": 50.00,
    "currency": "USD"
  }
}`} data-id="element-1637" />
        </>
  }];
  return <DocPageLayout title="<<hostedSession>> Integration" description="Simple iframe solution for a customizable payment form that keeps sensitive card data off your servers." data-id="element-1638">
      <p className="text-gray-700 mb-6" data-id="element-1639">
        The Hosted Session integration provides a flexible way to collect
        payment details using secure hosted input fields embedded directly into
        your payment page. Unlike the full Checkout redirect, Hosted Session
        lets you maintain complete control over the look and feel of your
        payment form while still reducing your PCI DSS scope.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1640">Benefits</h2>
      <ul className="list-disc list-inside space-y-2 text-gray-700 mb-8 ml-4" data-id="element-1641">
        <li data-id="element-1642">
          <strong data-id="element-1643">Customizable UI</strong> — Design your own payment form with
          your branding
        </li>
        <li data-id="element-1644">
          <strong data-id="element-1645">PCI DSS SAQ A-EP</strong> — Reduced compliance scope since
          card data is handled by hosted fields
        </li>
        <li data-id="element-1646">
          <strong data-id="element-1647">Seamless experience</strong> — Customers stay on your site
          throughout the payment process
        </li>
        <li data-id="element-1648">
          <strong data-id="element-1649">Flexible</strong> — Combine with other gateway features like
          tokenization and 3D Secure
        </li>
      </ul>

      <InfoBox type="warning" title="HTTPS Required" data-id="element-1650">
        Your website must be served over HTTPS to use Hosted Session. The
        gateway will refuse to render hosted fields on non-secure pages.
      </InfoBox>

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1651">
        Integration Steps
      </h2>
      <StepList steps={steps} data-id="element-1652" />

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1653">
        Session Parameters
      </h2>
      <div className="overflow-x-auto my-6" data-id="element-1654">
        <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden" data-id="element-1655">
          <thead data-id="element-1656">
            <tr className="bg-gray-50" data-id="element-1657">
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1658">
                Parameter
              </th>
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1659">
                Type
              </th>
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1660">
                Required
              </th>
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1661">
                Description
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100" data-id="element-1662">
            <tr data-id="element-1663">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1664">session</td>
              <td className="px-4 py-3" data-id="element-1665">String</td>
              <td className="px-4 py-3" data-id="element-1666">Yes</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1667">
                The session ID returned from the create session call
              </td>
            </tr>
            <tr className="bg-gray-50/50" data-id="element-1668">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1669">
                fields.card.number
              </td>
              <td className="px-4 py-3" data-id="element-1670">String</td>
              <td className="px-4 py-3" data-id="element-1671">Yes</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1672">
                CSS selector for the card number input container
              </td>
            </tr>
            <tr data-id="element-1673">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1674">
                fields.card.securityCode
              </td>
              <td className="px-4 py-3" data-id="element-1675">String</td>
              <td className="px-4 py-3" data-id="element-1676">Yes</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1677">
                CSS selector for the CVV input container
              </td>
            </tr>
            <tr className="bg-gray-50/50" data-id="element-1678">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1679">
                fields.card.expiryMonth
              </td>
              <td className="px-4 py-3" data-id="element-1680">String</td>
              <td className="px-4 py-3" data-id="element-1681">Yes</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1682">
                CSS selector for the expiry month input
              </td>
            </tr>
            <tr data-id="element-1683">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1684">
                fields.card.expiryYear
              </td>
              <td className="px-4 py-3" data-id="element-1685">String</td>
              <td className="px-4 py-3" data-id="element-1686">Yes</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1687">
                CSS selector for the expiry year input
              </td>
            </tr>
            <tr className="bg-gray-50/50" data-id="element-1688">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1689">
                frameEmbeddingMitigation
              </td>
              <td className="px-4 py-3" data-id="element-1690">Array</td>
              <td className="px-4 py-3" data-id="element-1691">No</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1692">
                Security options to prevent clickjacking
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <InfoBox type="tip" title="Styling Hosted Fields" data-id="element-1693">
        You can style the hosted input fields using the{' '}
        <code className="bg-green-100 px-1 rounded text-xs font-mono" data-id="element-1694">
          style
        </code>{' '}
        configuration option. Pass CSS properties as a JavaScript object to
        match your site's design.
      </InfoBox>
    </DocPageLayout>;
}